// ** Modules
import React from 'react';

// ** UI
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

// ** Components
import Divider from '@/components/common/Divider';
import EmployeeTable from './EmployeeTable';
import EmployeeFormDialog from './EmployeeFormDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';

// ** Styles
import { DIN } from '@/styles/globalStyles';

// ** Types
import type { Employee, EmployeeListBlockProps } from './EmployeeList.types';

const ROWS_PER_PAGE = 6;

const EmployeeListBlock = ({ title, addLabel, employees: initialEmployees, columns }: EmployeeListBlockProps) => {
	const theme = useTheme();
	const [employees, setEmployees] = React.useState(initialEmployees);
	const [page, setPage] = React.useState(1);
	const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());

	// Dialog state
	const [formOpen, setFormOpen] = React.useState(false);
	const [editingEmployee, setEditingEmployee] = React.useState<Employee | null>(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

	const totalPages = Math.max(1, Math.ceil(employees.length / ROWS_PER_PAGE));
	const paginatedEmployees = React.useMemo(
		() => employees.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE),
		[employees, page]
	);

	const selectedCount = selectedIds.size;

	const isAllSelected = paginatedEmployees.length > 0 && paginatedEmployees.every((e) => selectedIds.has(e.id));

	const isIndeterminate = !isAllSelected && paginatedEmployees.some((e) => selectedIds.has(e.id));

	const handleToggleItem = React.useCallback((id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const handleToggleAll = React.useCallback(() => {
		setSelectedIds((prev) => {
			const pageIds = paginatedEmployees.map((e) => e.id);
			const allPageSelected = pageIds.every((id) => prev.has(id));
			const next = new Set(prev);
			if (allPageSelected) {
				for (const id of pageIds) {
					next.delete(id);
				}
			} else {
				for (const id of pageIds) {
					next.add(id);
				}
			}
			return next;
		});
	}, [paginatedEmployees]);

	const handlePageChange = React.useCallback((_: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	}, []);

	// --- Add ---
	const handleOpenAdd = React.useCallback(() => {
		setEditingEmployee(null);
		setFormOpen(true);
	}, []);

	const handleAddSubmit = React.useCallback((values: Omit<Employee, 'id'>) => {
		const newEmployee: Employee = {
			...values,
			id: `emp-${Date.now()}`
		};
		setEmployees((prev) => [newEmployee, ...prev]);
	}, []);

	// --- Edit ---
	const handleOpenEdit = React.useCallback((employee: Employee) => {
		setEditingEmployee(employee);
		setFormOpen(true);
	}, []);

	const handleEditSubmit = React.useCallback(
		(values: Omit<Employee, 'id'>) => {
			if (!editingEmployee) return;
			setEmployees((prev) => prev.map((e) => (e.id === editingEmployee.id ? { ...e, ...values } : e)));
		},
		[editingEmployee]
	);

	const handleFormClose = React.useCallback(() => {
		setFormOpen(false);
	}, []);

	const handleFormExited = React.useCallback(() => {
		setEditingEmployee(null);
	}, []);

	// --- Delete ---
	const handleDeleteClick = React.useCallback(() => {
		if (selectedCount === 0) return;
		setDeleteConfirmOpen(true);
	}, [selectedCount]);

	const handleDeleteConfirm = React.useCallback(() => {
		setEmployees((prev) => prev.filter((e) => !selectedIds.has(e.id)));
		setSelectedIds(new Set());
		setDeleteConfirmOpen(false);
		const maxPage = Math.max(1, Math.ceil((employees.length - selectedIds.size) / ROWS_PER_PAGE));
		if (page > maxPage) setPage(maxPage);
	}, [selectedIds, employees.length, page]);

	return (
		<Box sx={{ width: '100%' }}>
			<Typography
				variant="title-md"
				component="h1"
				sx={{
					color: 'common.black',
					mb: 12
				}}
			>
				{title}
			</Typography>

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					mb: 6,
					pr: 12
				}}
			>
				<ButtonBase
					onClick={handleOpenAdd}
					disableRipple
					sx={{
						color: theme.palette.text.secondary,
						textDecoration: 'underline',
						textDecorationSkipInk: 'none',
						textUnderlineOffset: '2px',
						textDecorationThickness: '1px',
						transition: 'opacity 180ms ease',
						'&:hover': { opacity: 0.72 },
						'& .MuiTouchRipple-root': { display: 'none' }
					}}
				>
					<Typography variant="body-md" component="span">
						{addLabel}
					</Typography>
				</ButtonBase>

				<Badge
					overlap="circular"
					anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
					invisible={selectedCount === 0}
					badgeContent={selectedCount}
					sx={{
						'& .MuiBadge-badge': {
							minWidth: 18,
							height: 18,
							top: '-2px',
							right: '-1px',
							borderRadius: 999,
							backgroundColor: theme.palette.primary.main,
							color: theme.palette.common.white,
							fontSize: 12,
							fontWeight: 600
						}
					}}
				>
					<IconButton
						size="small"
						onClick={handleDeleteClick}
						disabled={selectedCount === 0}
						aria-label="Ausgewählte Mitarbeiter löschen"
						sx={{
							color: selectedCount > 0 ? theme.palette.text.secondary : theme.palette.text.disabled,
							p: 0.5,
							transition: 'color 160ms ease'
						}}
					>
						<DeleteOutlineOutlinedIcon />
					</IconButton>
				</Badge>
			</Box>

			<Divider color="divider" />

			<Typography aria-live="polite" aria-atomic="true" sx={visuallyHidden}>
				{selectedCount > 0
					? `${selectedCount} ${selectedCount === 1 ? 'Mitarbeiter ausgewählt' : 'Mitarbeiter ausgewählt'}`
					: ''}
			</Typography>

			{employees.length === 0 ? (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						pt: 12,
						pb: { xs: 16, md: 24 }
					}}
				>
					<PeopleOutlineOutlinedIcon
						aria-hidden="true"
						sx={{
							fontSize: 56,
							color: 'text.disabled',
							mb: 4
						}}
					/>
					<Typography variant="body-md" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
						Keine Mitarbeiter-Zugänge vorhanden.
					</Typography>
				</Box>
			) : (
				<>
					<Box sx={{ mt: 12 }}>
						<EmployeeTable
							employees={paginatedEmployees}
							columns={columns}
							selectedIds={selectedIds}
							page={page}
							onToggleItem={handleToggleItem}
							onToggleAll={handleToggleAll}
							onEdit={handleOpenEdit}
							isAllSelected={isAllSelected}
							isIndeterminate={isIndeterminate}
						/>
					</Box>

					<Divider color="divider" />

					<Box
						component="nav"
						aria-label="Seitennavigation Mitarbeiterliste"
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							pt: { xs: 4, md: 6 }
						}}
					>
						<Pagination
							count={totalPages}
							page={page}
							onChange={handlePageChange}
							color="standard"
							shape="rounded"
							getItemAriaLabel={(type, p, selected) => {
								if (type === 'page') return `${selected ? '' : 'Gehe zu '}Seite ${p}`;
								if (type === 'next') return 'Nächste Seite';
								if (type === 'previous') return 'Vorherige Seite';
								return '';
							}}
							sx={{
								'& .MuiPaginationItem-root': {
									fontFamily: DIN,
									fontWeight: 500,
									fontSize: '14px',
									color: theme.palette.text.primary,
									border: `1px solid ${theme.palette.divider}`,
									borderRadius: '4px',
									minWidth: 32,
									height: 32
								},
								'& .Mui-selected': {
									backgroundColor: `${theme.palette.common.black} !important`,
									color: `${theme.palette.common.white} !important`,
									borderColor: theme.palette.common.black
								},
								'& .MuiPaginationItem-previousNext': {
									border: 'none',
									transition: 'color 160ms ease, background-color 160ms ease',
									'&:hover': {
										bgcolor: 'action.hover',
										color: theme.palette.common.black
									}
								}
							}}
						/>
					</Box>
				</>
			)}

			{/* Add / Edit dialog */}
			<EmployeeFormDialog
				open={formOpen}
				onClose={handleFormClose}
				onExited={handleFormExited}
				onSubmit={editingEmployee ? handleEditSubmit : handleAddSubmit}
				employee={editingEmployee}
			/>

			{/* Delete confirmation dialog */}
			<DeleteConfirmDialog
				open={deleteConfirmOpen}
				count={selectedCount}
				onClose={() => setDeleteConfirmOpen(false)}
				onConfirm={handleDeleteConfirm}
			/>
		</Box>
	);
};

export default EmployeeListBlock;
